import React, { useState, useEffect } from "react";
import moment from "moment";
import { getTimesheet } from "../../services";
import { useSelector } from "react-redux";

export default function DashWorkingHistory() {
  const [timesheetData, setTimesheetData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const userProfile = useSelector((state) => state.user.userinfo);

  const fetchTime = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const today = moment().subtract(0, 'days').format('YYYY-MM-DD');
      const last7Days = moment().subtract(7, 'days').format('YYYY-MM-DD');
      const response = await getTimesheet(
        userProfile.Id,
        last7Days,
        today,
        0,
        3
      );
      setTimesheetData(response?.data?.Result?.response || []);
    } catch (err) {
      setError("Failed to fetch working history");
      console.error("Error fetching timesheet:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTime();
  }, []);

  const getEffectiveTimeColor = (totalHours, workHours) => {
    const total = parseInt(totalHours);
    const required = parseInt(workHours);
    if (total >= required) return "text-green-600 dark:text-green-400";
    if (total >= required * 0.8) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  return (
    <div className="relative mt-3 group/dashboard-working-history">
      <div
        className="absolute -inset-[1px] bg-gradient-to-r from-blue-600/40 via-indigo-600/40 to-blue-600/40 rounded-2xl blur-sm opacity-50 group-hover/dashboard-working-history:opacity-80 transition duration-500 pointer-events-none"
        aria-hidden="true"
      />
      <div className="relative bg-white/70 dark:bg-black/40 backdrop-blur-3xl rounded-2xl min-h-56 border-2 border-blue-500/20 dark:border-blue-400/20 shadow-[0_15px_35px_-10px_rgba(0,0,0,0.4)]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-blue-500/10">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Working History</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Last 2 days attendance record</p>
          </div>
          <button
            type="button"
            onClick={fetchTime}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            title="Refresh"
          >
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-6">
            <div className="text-red-500 dark:text-red-400 text-sm mb-2">{error}</div>
            <button
              type="button"
              onClick={fetchTime}
              className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
            >
              Try Again
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-white/5 dark:bg-white/5 text-left">
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">DATE</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">ARRIVAL</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">DEPARTURE</th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-300">EFFECTIVE TIME</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {timesheetData.length > 0 ? (
                  timesheetData
                    .filter((entry) => !moment(entry.CheckInTime).isSame(moment(), "day"))
                    .map((entry, index) => (
                      <tr
                        key={index}
                        className="hover:bg-white/[0.06] dark:hover:bg-white/[0.08] transition-colors"
                      >
                        <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-gray-200">
                          {moment(entry.CheckInTime).format("DD/MM/YYYY")}
                        </td>
                        <td className="px-4 py-2.5 text-sm text-gray-900 dark:text-gray-200">
                          {moment(entry.CheckInTime).format("h:mm A")}
                        </td>
                        <td className="px-4 py-2.5 text-sm">
                          {entry.CheckOutTime ? (
                            <span className="text-gray-900 dark:text-gray-200">
                              {moment(entry.CheckOutTime).format("h:mm A")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full border border-green-400/25 bg-green-500/15 px-2 py-1 text-xs font-medium text-green-700 dark:border-green-400/20 dark:bg-green-500/10 dark:text-green-200">
                              <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />
                              Active
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-col">
                            <span className={`text-sm font-medium ${getEffectiveTimeColor(entry.TotalHours, entry.workHours)}`}>
                              {entry.TotalHours}h {entry.TotalMins}m
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              Target: {entry.workHours}h
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))
                ) : (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center">
                      <div className="text-gray-500 dark:text-gray-400">
                        <svg className="w-6 h-6 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <p className="text-sm font-medium">No working history available</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}